import { useRef, useState } from "react";
import { Camera, Trash2, Upload, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading, Spinner } from "@/components/States";

export async function uploadFile(file, projectId, kind) {
  const fd = new FormData();
  fd.append("file", file); fd.append("project_id", projectId); fd.append("kind", kind);
  return (await api.post("/files", fd)).data;
}

import PhotoMarkup from "@/components/project/PhotoMarkup";

export default function PhotosTab({ projectId }) {
  const { user } = useAuth();
  const { data, error, loading, reload } = useFetch(`/projects/${projectId}/photos`);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [markup, setMarkup] = useState(null);
  const ref = useRef();
  const canUpload = user.role !== "client";

  const onFiles = async (files) => {
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try { await uploadFile(f, projectId, "photo"); ok++; } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded`);
    setBusy(false); reload();
  };
  const remove = async (p) => {
    try { await api.delete(`/files/${p.file_id}`); toast.success("Photo removed"); setView(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      {canUpload && (
        <div>
          <input ref={ref} type="file" accept="image/*" multiple hidden onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} data-testid="photo-file-input" />
          <Button className="rounded-full" onClick={() => ref.current.click()} disabled={busy} data-testid="upload-photos-button">{busy ? <Spinner /> : <Upload className="mr-1 h-4 w-4" />}Upload photos</Button>
        </div>
      )}
      {loading ? <Loading rows={2} /> : error ? <ErrorState message={error} onRetry={reload} /> : data.length === 0 ? (
        <Empty icon={Camera} title="No photos yet" text="Site photos, progress shots and inspiration images will appear here." />
      ) : (
        <div className="columns-2 gap-4 md:columns-3 xl:columns-4" data-testid="photos-grid">
          {data.map((p) => (
            <button key={p.id} onClick={() => setView(p)} className="lift mb-4 block w-full overflow-hidden rounded-lg border bg-card text-left" data-testid={`photo-${p.file_id}`}>
              <img src={fileUrl(p.url)} alt={p.original_filename} loading="lazy" className="w-full" />
              <p className="truncate px-3 py-2 text-xs text-muted-foreground">{p.uploaded_by_name} · {p.created_at?.slice(0, 10)}</p>
            </button>
          ))}
        </div>
      )}
      {markup && <PhotoMarkup photo={markup} projectId={projectId} onClose={() => setMarkup(null)} onSaved={reload} />}
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-4xl">
          <DialogTitle className="truncate text-sm font-medium">{view?.original_filename}</DialogTitle>
          {view && <img src={fileUrl(view.url)} alt="" className="max-h-[70vh] w-full rounded-md object-contain" />}
          <div className="flex flex-wrap gap-2">
            {view && canUpload && <Button size="sm" className="w-fit rounded-full" onClick={() => { setMarkup(view); setView(null); }} data-testid="markup-photo-button"><PenLine className="mr-1 h-4 w-4" />Mark up</Button>}
            {view && (user.role === "admin" || view.uploaded_by === user.id) && (
              <Button variant="outline" size="sm" className="w-fit" onClick={() => remove(view)} data-testid="delete-photo-button"><Trash2 className="mr-1 h-4 w-4" />Delete photo</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
